import React, { createContext, useContext, useState, useEffect } from 'react';
import { socketService } from '../services/SocketService';

const JWTAuthContext = createContext();

export const useJWTAuth = () => {
  const context = useContext(JWTAuthContext);
  if (!context) {
    throw new Error('useJWTAuth must be used within JWTAuthProvider');
  }
  return context;
};

export const JWTAuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load token from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('jwt_token');
    if (savedToken) {
      setToken(savedToken);
      // Verify token is still valid
      verifyToken(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await fetch('/api/auth/verify', {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('🔐 [JWT] Token verified, user data:', data.data.user);
          setUser(data.data.user);
          setLoading(false);
          
          // Socket connection will be established when user navigates to Heroes page
          
          return true;
        }
      }
      
      // Token is invalid, clear it
      logout();
      return false;
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
      return false;
    }
  };

  const login = async (walletAddress, username = null, password = null) => {
    console.log('🔐 [JWT] Login called:', {
      walletAddress: walletAddress ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` : 'none',
      hasUsername: !!username,
      username: username || 'none',
      hasPassword: !!password
    });

    // If password provided, use password login endpoint
    if (password) {
      try {
        const response = await fetch('/api/auth/login-password', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
          },
          body: JSON.stringify({ username, password })
        });

        const data = await response.json();
        
        if (data.success) {
          const { token: newToken, user: userData } = data.data;
          console.log('✅ [JWT] Password login successful, user data:', userData);
          setToken(newToken);
          setUser(userData);
          localStorage.setItem('jwt_token', newToken);
          
          return { success: true, user: userData };
        } else {
          console.log('❌ [JWT] Password login failed:', data.error);
          return { success: false, error: data.error };
        }
      } catch (error) {
        console.error('Password login failed:', error);
        return { success: false, error: 'Login failed' };
      }
    }

    // Otherwise, use wallet login (existing logic)
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({ walletAddress, username })
      });

      //console.log('🔐 [JWT] Login response status:', response.status);
      const data = await response.json();
      //console.log('🔐 [JWT] Login response data:', data);
      
      if (data.success) {
        // Check if username is needed
        if (data.needsUsername) {
          console.log('👤 [JWT] Username needed, returning needsUsername response');
          return { 
            success: true, 
            needsUsername: true, 
            message: data.message,
            walletAddress 
          };
        }

        const { token: newToken, user: userData } = data.data;
        //console.log('✅ [JWT] Login successful, user data:', userData);
        setToken(newToken);
        setUser(userData);
        localStorage.setItem('jwt_token', newToken);
        
        // Socket connection will be established when user navigates to Heroes page
        
        return { success: true, user: userData };
      } else {
        console.log('❌ [JWT] Login failed:', data.error);
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: 'Login failed' };
    }
  };

  const logout = () => {
    //console.log('🔐 [JWT] Logging out - clearing JWT token and user data');
    //console.log('🔐 [JWT] Current token before logout:', token ? 'exists' : 'null');
    //console.log('🔐 [JWT] Current user before logout:', user ? user.username : 'null');
    
    // Disconnect socket if connected
    if (socketService.isSocketConnected()) {
      socketService.disconnect();
      console.log('🔐 [JWT] Socket disconnected');
    }
    
    // Clear localStorage
    localStorage.removeItem('jwt_token');
    console.log('🔐 [JWT] localStorage cleared');
    
    // Clear React state - use functional updates to ensure they happen
    setToken(prevToken => {
      console.log('🔐 [JWT] Setting token to null, previous:', prevToken ? 'exists' : 'null');
      return null;
    });
    setUser(prevUser => {
      console.log('🔐 [JWT] Setting user to null, previous:', prevUser ? prevUser.username : 'null');
      return null;
    });
    
    console.log('✅ [JWT] Logout complete');
  };

  const isAuthenticated = () => {
    // If still loading, don't consider authenticated yet (prevents false positives during token verification)
    if (loading) {
      return false;
    }
    
    const hasToken = !!(token && user);
    const localStorageToken = localStorage.getItem('jwt_token');
    
    // Only log warning if we're not loading and there's a mismatch
    if (!loading && hasToken !== !!localStorageToken) {
      console.warn('🔐 [JWT] State mismatch detected:', {
        contextToken: !!token,
        contextUser: !!user,
        hasToken,
        localStorageToken: localStorageToken ? 'exists' : 'null',
        loading
      });
    }
    
    return hasToken;
  };

  const getAuthHeaders = () => {
    if (!token) return {};
    return {
      'Authorization': `Bearer ${token}`,
      'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
    };
  };

  const value = {
    token,
    user,
    loading,
    login,
    logout,
    isAuthenticated,
    getAuthHeaders,
    verifyToken
  };

  return (
    <JWTAuthContext.Provider value={value}>
      {children}
    </JWTAuthContext.Provider>
  );
};
